import type { NitroFetchOptions } from 'nitropack'
import { apiInstance } from '../instance'
import { tokenSchema, type Token } from '../schemas/token'
import { useApiFactory } from '../use-api-factory'

type Username = 'app' | 'backAdmin'

export function useAuthApi() {
  const api = useApiFactory(apiInstance, {
    useToken: false
  })

  function getFetchOptions(
    username: Username,
    password: string
  ): NitroFetchOptions<string> {
    return {
      headers: {
        Authorization: `Basic ${btoa(username + ':' + password)}`
      }
    }
  }

  /** 登入 */
  function login(data: { username: string; password: string }) {
    const { username, password } = data
    const requestData = { ...data, grant_type: 'password' }
    const url = `oauth/token?grant_type=password&username=${username}&password=${password}`
    return api.post<Token>(
      url,
      requestData,
      getFetchOptions('backAdmin', '27714944')
    )
  }

  /** 取得未登入 Token */
  function getNoLoginToken() {
    const url = `oauth/token?grant_type=password&username=&password=`
    const data = { username: '', password: '', grant_type: 'password' }
    return api.post(url, data, getFetchOptions('app', '27714944'), {
      responseSchema: tokenSchema
    })
  }

  /** 使用 Refresh Token 取得新 Token */
  function refreshToken(username: Username) {
    const { refreshToken } = storeToRefs(useAuthStore())

    const data = {
      refresh_token: refreshToken.value,
      grant_type: 'refresh_token'
    }

    const url = `oauth/token?grant_type=refresh_token&refresh_token=${refreshToken.value}`

    return api.post(url, data, getFetchOptions(username, '27714944'), {
      responseSchema: tokenSchema
    })
  }

  /** 刷新前台 Token */
  function refreshAppToken() {
    return new Promise<any>((resolve, reject) => {
      refreshToken('app')
        .then((response) => {
          const data = response
          useAuthStore().setStoreAndCookies({
            accessToken: data.access_token,
            account: data.accountNo,
            refreshToken: data.refresh_token
          })
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /** 刷新後台 Token */
  function refreshAdminToken() {
    return new Promise<any>((resolve, reject) => {
      refreshToken('backAdmin')
        .then((response) => {
          const data = response
          useAuthStore().setStoreAndCookies({
            accessToken: data.access_token,
            account: data.accountNo,
            refreshToken: data.refresh_token
          })
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /** 取得前台 Token */
  async function getAppToken() {
    const data = await getNoLoginToken()

    useAuthStore().setStoreAndCookies({
      accessToken: data.access_token || '',
      account: data.accountNo === 'user' ? '' : data.accountNo,
      refreshToken: data.refresh_token || ''
    })
  }

  /** 取得後台 Token */
  function getAdminToken(options: { username: string; password: string }) {
    const { username, password } = options

    return new Promise<any>((resolve, reject) => {
      login({ username, password })
        .then((response) => {
          useAuthStore().setStoreAndCookies({
            accessToken: response.access_token,
            account: response.accountNo,
            refreshToken: response.refresh_token
          })

          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  return {
    login,
    refreshToken,
    refreshAppToken,
    refreshAdminToken,
    getNoLoginToken,
    getAppToken,
    getAdminToken
  }
}
